import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Tabs,
  Space,
  Tag,
  Avatar,
  Popconfirm,
  Typography,
  Descriptions,
  List,
  Dropdown,
  Menu,
  Spin,
  Switch,
  Popover,
  Alert,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  UserOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EllipsisOutlined,
  GlobalOutlined,
  CopyOutlined,
  KeyOutlined,
  ReloadOutlined,
  CameraOutlined,
  LockOutlined,
  RightOutlined,
  DownOutlined,
  SaveOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  ShopOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import apiClient from "../api/apiClient";
import WhatsAppPhoneInput from "../components/WhatsAppPhoneInput";
import WhatsAppRecapSettings from "../components/WhatsAppRecapSettings";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import {
  isValidOptionalWhatsAppPhone,
  WHATSAPP_PHONE_VALIDATION_MESSAGE,
} from "../utils/whatsappPhone";
import { useSelection } from "../SelectionContext"; // Assuming this context provides selectedCompany and selectedWarehouse
import { useAuth } from "../context/AuthContext"; // Use AuthContext
import API_CONFIG from '../config/api.js';
import {
  copyCredential,
  generateSecurePassword,
} from "../utils/staffPassword";
import "./GestionStaff.css";

const { TabPane } = Tabs;
const { Option, OptGroup } = Select;
const { Text } = Typography;

// --- Helper function to construct absolute image URL ---
const BACKEND_URL = API_CONFIG.BASE_URL;

const getImageUrl = (imagePath) => {
  if (!imagePath) return undefined;
  if (imagePath.startsWith("http")) return imagePath; // Already absolute

  // Ensure path starts with '/' if it's relative to domain root
  // Assumes backend paths are like '/uploads/profiles/filename.png' or just 'filename.png'
  const path = imagePath.startsWith("/")
    ? imagePath
    : `/uploads/profiles/${imagePath}`;

  return `${BACKEND_URL}${path}`;
};
// --- End Helper ---

const beforeStaffImageUpload = (file) => {
  const isSupportedImage =
    ["image/jpeg", "image/png"].includes(file.type) ||
    /\.(jpe?g|png)$/i.test(file.name);

  if (!isSupportedImage) {
    message.error("Veuillez sélectionner une image au format PNG ou JPG.");
    return Upload.LIST_IGNORE;
  }

  const isWithinSizeLimit = file.size / 1024 / 1024 <= 5;
  if (!isWithinSizeLimit) {
    message.error("L’image ne doit pas dépasser 5 Mo.");
    return Upload.LIST_IGNORE;
  }

  return false;
};

const StaffProfileImageUploader = ({ fileList = [], onChange, disabled }) => {
  const [previewUrl, setPreviewUrl] = useState();
  const currentFile = fileList[0];

  useEffect(() => {
    let objectUrl;

    if (currentFile?.originFileObj) {
      objectUrl = URL.createObjectURL(currentFile.originFileObj);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(currentFile?.thumbUrl || currentFile?.url);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [currentFile]);

  return (
    <div className="staff-editor-modal__photo-card">
      <Avatar
        alt={currentFile?.name || "Photo de profil"}
        className="staff-editor-modal__photo-avatar"
        size={88}
        src={previewUrl}
        icon={<UserOutlined />}
      />

      <div className="staff-editor-modal__photo-details">
        <span
          className="staff-editor-modal__photo-name"
          title={currentFile?.name || "Aucune image sélectionnée"}
        >
          {currentFile?.name || "Aucune image sélectionnée"}
        </span>

        <Upload
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          beforeUpload={beforeStaffImageUpload}
          disabled={disabled}
          fileList={fileList}
          maxCount={1}
          onChange={onChange}
          showUploadList={false}
        >
          <Button
            className="staff-editor-modal__photo-button"
            disabled={disabled}
            icon={<CameraOutlined />}
          >
            {currentFile ? "Changer la photo" : "Ajouter une photo"}
          </Button>
        </Upload>

        <span className="staff-editor-modal__photo-help">
          PNG ou JPG · 5 Mo maximum
        </span>
      </div>
    </div>
  );
};

const StaffModalSectionHeader = ({ id, icon, title, description, meta }) => (
  <div className="staff-editor-modal__section-header">
    <span className="staff-editor-modal__section-icon" aria-hidden="true">
      {icon}
    </span>
    <div className="staff-editor-modal__section-heading">
      <h3 id={id}>{title}</h3>
      {description ? <p>{description}</p> : null}
    </div>
    {meta ? <div className="staff-editor-modal__section-meta">{meta}</div> : null}
  </div>
);

const AccessSelectionPopover = ({ title, items }) => (
  <div className="staff-editor-modal__selection-popover">
    <strong>{title}</strong>
    <div className="staff-editor-modal__selection-list">
      {items.map((item) => (
        <Tag key={item.key}>{item.label}</Tag>
      ))}
    </div>
  </div>
);

const formatCountLabel = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : plural}`;

const formatViewLabel = (count, singular, plural) =>
  count === 1 ? `Voir ${singular}` : `Voir les ${count} ${plural}`;

const getStaffSaveErrorMessage = (error) => {
  if (["ECONNABORTED", "ETIMEDOUT"].includes(error?.code)) {
    return "Le serveur met trop de temps à répondre. Vérifiez la fiche du membre avant de réessayer.";
  }
  if (error?.code === "ERR_NETWORK" || (!error?.response && error?.request)) {
    return "La connexion au serveur a été interrompue. Vérifiez votre connexion et la fiche du membre avant de réessayer.";
  }
  const data = error?.response?.data;
  const explanation = [data?.error, data?.message].find(
    (value) => typeof value === "string" && value.trim(),
  );
  return explanation || "Impossible d’enregistrer ce membre du personnel. Réessayez ; si le problème persiste, contactez l’administrateur.";
};

// --- Permissions Keys ---
const STAFF_PERMISSIONS = {
  VIEW: "Admin.GestionUtilisateurs.view",
  CREATE: "Admin.GestionUtilisateurs.create",
  EDIT: "Admin.GestionUtilisateurs.edit",
  DELETE: "Admin.GestionUtilisateurs.delete",
  ASSIGN_ROLE: "Admin.GestionUtilisateurs.assign_role", // Added if needed elsewhere
};
// -----------------------

const GestionStaff = () => {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showRoleErrors, setShowRoleErrors] = useState(false);
  const companyRoleFieldRefs = useRef(new Map());
  const [editingUser, setEditingUser] = useState(null);
  const [activeTabKey, setActiveTabKey] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState(""); // Existing state for search
  const [form] = Form.useForm();
  const { selectedCompany } = useSelection(); // Get selected company ID
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [accessSummary, setAccessSummary] = useState(null);
  const { user, hasPermission } = useAuth(); // Get user and hasPermission from AuthContext
  const queryClient = useQueryClient();
  const editRequestRef = useRef(0);
  const currentCompanyRef = useRef(selectedCompany);
  currentCompanyRef.current = selectedCompany;
  useEffect(() => {
    editRequestRef.current += 1;
    setIsModalVisible(false);
    setEditingUser(null);
  }, [selectedCompany]);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [usernameSuggestionLoading, setUsernameSuggestionLoading] = useState(false);
  const [usernameSuggestionError, setUsernameSuggestionError] = useState("");
  const [usernameWasEdited, setUsernameWasEdited] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(true);
  const usernameWasEditedRef = useRef(false);
  const usernameSuggestionRequestRef = useRef(0);
  const usernameSuggestionNameRef = useRef("");
  const rolesRequestRef = useRef(0);
  const [unavailableRoleCompanyIds, setUnavailableRoleCompanyIds] = useState([]);
  const [whatsappRecapWarehouses, setWhatsappRecapWarehouses] = useState([]);
  const [companyRoleEditor, setCompanyRoleEditor] = useState(null);
  const [companyRoleIds, setCompanyRoleIds] = useState([]);
  const [companyRoleSaving, setCompanyRoleSaving] = useState(false);
  const primaryCompanyId = Number(editingUser?.company_id || selectedCompany);
  const watchedRoleIds = Form.useWatch("role_ids", form);
  const selectedRoleIds = useMemo(() => watchedRoleIds || [], [watchedRoleIds]);
  const selectedCompanyIds = Form.useWatch("company_ids", form);
  const selectedWarehouseIds = Form.useWatch("warehouse_ids", form);
  const watchedName = Form.useWatch("name", form);
  const watchedPhone = Form.useWatch("phone", form);
  const watchedPassword = Form.useWatch("password", form);
  const watchedPasswordConfirmation = Form.useWatch(
    "password_confirmation",
    form,
  );

  const selectedRoleIsGlobalSysAdmin = useMemo(() => {
    return roles.some(
      (role) =>
        selectedRoleIds.map(Number).includes(Number(role.id)) &&
        role.name === "SysAdmin" &&
        !role.company_id,
    );
  }, [roles, selectedRoleIds]);

  const normalizedSelectedCompanyIds = useMemo(() => {
    const ids = Array.isArray(selectedCompanyIds) ? selectedCompanyIds : [];
    const normalizedIds = ids
      .map((companyId) => Number(companyId))
      .filter((companyId) => Number.isInteger(companyId) && companyId > 0);
    if (primaryCompanyId && !normalizedIds.includes(primaryCompanyId)) {
      normalizedIds.unshift(primaryCompanyId);
    }
    return [...new Set(normalizedIds)];
  }, [primaryCompanyId, selectedCompanyIds]);

  const companyById = useMemo(
    () => new Map(companies.map((company) => [Number(company.id), company])),
    [companies],
  );

  const warehouseById = useMemo(
    () =>
      new Map(warehouses.map((warehouse) => [Number(warehouse.id), warehouse])),
    [warehouses],
  );

  const normalizedSelectedWarehouseIds = useMemo(() => {
    const ids = Array.isArray(selectedWarehouseIds) ? selectedWarehouseIds : [];
    return [
      ...new Set(
        ids
          .map((warehouseId) => Number(warehouseId))
          .filter((warehouseId) => Number.isInteger(warehouseId) && warehouseId > 0),
      ),
    ];
  }, [selectedWarehouseIds]);

  const selectedCompanyItems = useMemo(
    () =>
      normalizedSelectedCompanyIds
        .map((companyId) => {
          const company = companyById.get(companyId);
          if (!company) return null;
          return {
            key: companyId,
            label: `${company.name}${
              companyId === primaryCompanyId ? " — principale" : ""
            }`,
          };
        })
        .filter(Boolean),
    [companyById, normalizedSelectedCompanyIds, primaryCompanyId],
  );

  const selectedWarehouseItems = useMemo(
    () =>
      normalizedSelectedWarehouseIds
        .map((warehouseId) => {
          const warehouse = warehouseById.get(warehouseId);
          if (!warehouse) return null;
          const companyName = companyById.get(Number(warehouse.company_id))?.name;
          return {
            key: warehouseId,
            label: companyName
              ? `${warehouse.name} — ${companyName}`
              : warehouse.name,
          };
        })
        .filter(Boolean),
    [companyById, normalizedSelectedWarehouseIds, warehouseById],
  );

  const warehousesByCompany = useMemo(() => {
    const grouped = new Map();
    warehouses.forEach((warehouse) => {
      const companyId = Number(warehouse.company_id);
      if (!grouped.has(companyId)) grouped.set(companyId, []);
      grouped.get(companyId).push(warehouse);
    });
    grouped.forEach((items) =>
      items.sort((left, right) =>
        String(left.name).localeCompare(String(right.name), "fr"),
      ),
    );
    return grouped;
  }, [warehouses]);

  // Effet pour mettre à jour isSmallScreen lors du redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Determine if the LOGGED IN user is SysAdmin
  const loggedInUserIsSysAdmin =
    user?.is_superadmin === true || Number(user?.is_superadmin) === 1;

  const requestUsernameSuggestion = useCallback(
    async (name, { force = false } = {}) => {
      const normalizedName = String(name || "").trim();
      if (!loggedInUserIsSysAdmin || editingUser || !normalizedName) return;

      const requestId = usernameSuggestionRequestRef.current + 1;
      usernameSuggestionRequestRef.current = requestId;
      setUsernameSuggestionLoading(true);
      setUsernameSuggestionError("");

      try {
        const response = await apiClient.get(
          "/api/users/username-suggestion",
          { params: { name: normalizedName } },
        );
        if (requestId !== usernameSuggestionRequestRef.current) return;
        if (!force && usernameWasEditedRef.current) return;

        const username = String(response.data?.username || "").trim();
        if (username) {
          usernameSuggestionNameRef.current = normalizedName;
          usernameWasEditedRef.current = false;
          setUsernameWasEdited(false);
          form.setFieldValue("email", username);
        }
      } catch (error) {
        if (requestId !== usernameSuggestionRequestRef.current) return;
        setUsernameSuggestionError(
          error.response?.data?.error ||
            "La proposition automatique est indisponible. Saisissez l’identifiant manuellement.",
        );
      } finally {
        if (requestId === usernameSuggestionRequestRef.current) {
          setUsernameSuggestionLoading(false);
        }
      }
    },
    [editingUser, form, loggedInUserIsSysAdmin],
  );

  useEffect(() => {
    if (
      !isModalVisible ||
      editingUser ||
      !loggedInUserIsSysAdmin ||
      usernameWasEdited
    ) {
      return undefined;
    }

    const normalizedName = String(watchedName || "").trim();
    if (!normalizedName) {
      usernameSuggestionNameRef.current = "";
      form.setFieldValue("email", "");
      return undefined;
    }
    if (usernameSuggestionNameRef.current === normalizedName) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      requestUsernameSuggestion(normalizedName);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    editingUser,
    form,
    isModalVisible,
    loggedInUserIsSysAdmin,
    requestUsernameSuggestion,
    usernameWasEdited,
    watchedName,
  ]);

  const handleSuggestUsername = useCallback(() => {
    usernameWasEditedRef.current = false;
    requestUsernameSuggestion(form.getFieldValue("name"), { force: true });
  }, [form, requestUsernameSuggestion]);

  const handleFormValuesChange = useCallback(
    (changedValues) => {
      if (Object.prototype.hasOwnProperty.call(changedValues, "email")) {
        usernameWasEditedRef.current = true;
        usernameSuggestionRequestRef.current += 1;
        setUsernameWasEdited(true);
        setUsernameSuggestionLoading(false);
        setUsernameSuggestionError("");
      }
      if (
        Object.prototype.hasOwnProperty.call(changedValues, "password") &&
        changedValues.password !== generatedPassword
      ) {
        setGeneratedPassword("");
      }
    },
    [generatedPassword],
  );

  const handleGeneratePassword = useCallback(() => {
    try {
      const password = generateSecurePassword(16);
      form.setFieldsValue({
        password,
        password_confirmation: password,
      });
      setGeneratedPassword(password);
      message.success("Mot de passe sécurisé généré.");
    } catch (error) {
      message.error(error.message || "Impossible de générer le mot de passe.");
    }
  }, [form]);

  const handleCopyGeneratedPassword = useCallback(async () => {
    try {
      await copyCredential(generatedPassword);
      message.success("Mot de passe copié.");
    } catch (error) {
      message.warning(
        "Copie automatique indisponible. Affichez puis copiez le mot de passe manuellement.",
      );
    }
  }, [generatedPassword]);

  const fetchStaff = useCallback(
    async (
      page = pagination.current,
      pageSize = pagination.pageSize,
      status = activeTabKey,
      search = searchTerm
    ) => {
      if (!selectedCompany) {
        setStaff([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
        return; // Don't fetch if no company selected
      }
      setLoading(true);
      try {
        const params = {
          page,
          limit: pageSize,
          user_type: "staff_members", // Always fetch staff members
          companyId: selectedCompany,
          search: search || undefined,
          status: status === "all" ? undefined : status, // 'enabled' or 'disabled'
        };
        const response = await apiClient.get("/api/users", { params });
        setStaff(response.data.users || []);
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.itemsPerPage,
          total: response.data.pagination.totalItems,
        });
      } catch (error) {
        message.error(
          "Erreur lors de la récupération des données du personnel."
        );
        setStaff([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      } finally {
        setLoading(false);
      }
    },
    [
      selectedCompany,
      pagination.current,
      pagination.pageSize,
      activeTabKey,
      searchTerm,
    ]
  );

  const fetchRoles = useCallback(async (companyIds = [selectedCompany]) => {
    const requestedCompanyIds = [...new Set(
      (Array.isArray(companyIds) ? companyIds : [companyIds])
        .map(Number)
        .filter((companyId) => Number.isInteger(companyId) && companyId > 0),
    )];
    const requestId = ++rolesRequestRef.current;
    if (!requestedCompanyIds.length) {
      setRoles([]);
      return [];
    }
    try {
      const results = await Promise.allSettled(requestedCompanyIds.map((companyId) =>
        apiClient.get("/api/roles", {
          params: { company_id: companyId },
          headers: { "X-Company-Id": String(companyId) },
        }),
      ));
      const byId = new Map();
      results.filter((result) => result.status === "fulfilled")
        .forEach((result) => (result.value.data.roles || []).forEach((role) => {
        byId.set(Number(role.id), role);
        }));
      if (results.some((result) => result.status === "rejected") &&
          requestId === rolesRequestRef.current) {
        message.warning("Certains profils ne sont pas modifiables depuis votre contexte actuel.");
      }
      const fetchedRoles = [...byId.values()];
      if (requestId === rolesRequestRef.current) {
        setUnavailableRoleCompanyIds(requestedCompanyIds.filter((_, index) => results[index].status === "rejected"));
        setRoles((previousRoles) => {
        const selectedIds = new Set((form.getFieldValue("role_ids") || []).map(Number));
        const retained = isModalVisible
          ? previousRoles.filter((role) => selectedIds.has(Number(role.id)))
          : [];
        return [...new Map([...fetchedRoles, ...retained]
          .map((role) => [Number(role.id), role])).values()];
        });
      }
      return fetchedRoles;
    } catch (error) {
      if (requestId === rolesRequestRef.current) {
        message.error("Impossible de charger les rôles des entreprises sélectionnées.");
        setRoles([]);
      }
      return [];
    }
  }, [form, isModalVisible, selectedCompany]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/companies/for-modals", {
        params: { status: "all", limit: 1000 },
      });
      const fetchedCompanies = response.data.companies || [];
      const knownCompanies = new Map(
        fetchedCompanies.map((company) => [Number(company.id), company]),
      );

      (user?.assigned_warehouses || []).forEach((warehouse) => {
        const companyId = Number(warehouse.company_id);
        if (companyId > 0 && !knownCompanies.has(companyId)) {
          knownCompanies.set(companyId, {
            id: companyId,
            name: warehouse.company_name || `Entreprise ID ${companyId}`,
            status: "active",
          });
        }
      });

      const normalizedCompanies = Array.from(knownCompanies.values()).sort(
        (left, right) =>
          String(left.name).localeCompare(String(right.name), "fr"),
      );
      setCompanies(normalizedCompanies);
      return normalizedCompanies;
    } catch (error) {
      message.error("Impossible de charger les entreprises accessibles.");
      setCompanies([]);
      return [];
    }
  }, [user?.assigned_warehouses]);

  const fetchWarehouses = useCallback(
    async (companyIds = [selectedCompany]) => {
      const normalizedCompanyIds = [
        ...new Set(
          (Array.isArray(companyIds) ? companyIds : [companyIds])
            .map((companyId) => Number(companyId))
            .filter((companyId) => Number.isInteger(companyId) && companyId > 0),
        ),
      ];
      if (normalizedCompanyIds.length === 0) {
        setWarehouses([]);
        return [];
      }

      setScopeLoading(true);
      try {
        const results = await Promise.allSettled(
          normalizedCompanyIds.map((companyId) =>
            apiClient.get("/api/warehouses", {
              params: { company_id: companyId, page: 1, limit: 1000 },
              headers: { "X-Company-Id": String(companyId) },
            }),
          ),
        );
        const responses = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        if (responses.length !== results.length) {
          message.warning(
            "Certains établissements ne sont plus accessibles. Retirez l’entreprise concernée si nécessaire.",
          );
        }
        const fetchedWarehouses = responses
          .flatMap((response) => response.data.warehouses || [])
          .filter((warehouse) => warehouse.status === "active");
        setWarehouses(fetchedWarehouses);
        return fetchedWarehouses;
      } catch (error) {
        message.error("Impossible de charger les établissements.");
        setWarehouses([]);
        return [];
      } finally {
        setScopeLoading(false);
      }
    },
    [selectedCompany],
  );

  useEffect(() => {
    if (selectedCompany) {
      fetchStaff();
    } else {
      setStaff([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    }
  }, [fetchStaff, selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchRoles();
      fetchCompanies();
    } else {
      setRoles([]);
      setCompanies([]);
      setWarehouses([]);
      setEditingUser(null);
      setIsModalVisible(false);
    }
  }, [selectedCompany, fetchRoles, fetchCompanies]);

  useEffect(() => {
    if (
      !isModalVisible ||
      selectedRoleIsGlobalSysAdmin ||
      normalizedSelectedCompanyIds.length === 0
    ) {
      return;
    }
    fetchWarehouses(normalizedSelectedCompanyIds);
  }, [
    isModalVisible,
    selectedRoleIsGlobalSysAdmin,
    normalizedSelectedCompanyIds,
    fetchWarehouses,
  ]);

  useEffect(() => {
    if (isModalVisible && normalizedSelectedCompanyIds.length) {
      fetchRoles(normalizedSelectedCompanyIds);
    }
  }, [fetchRoles, isModalVisible, normalizedSelectedCompanyIds]);

  const handleTableChange = useCallback((newPagination, filters, sorter) => {
    fetchStaff(
      newPagination.current,
      newPagination.pageSize,
      activeTabKey,
      searchTerm
    );
  }, [fetchStaff, activeTabKey, searchTerm]);

  const handleTabChange = useCallback((key) => {
    setActiveTabKey(key);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchStaff(1, pagination.pageSize, key, searchTerm);
  }, [fetchStaff, pagination.pageSize, searchTerm]);

  const handleSearch = useCallback((value) => {
    const trimmedValue = value.trim();
    setSearchTerm(trimmedValue);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchStaff(1, pagination.pageSize, activeTabKey, trimmedValue);
  }, [fetchStaff, pagination.pageSize, activeTabKey]);

  const showModal = useCallback(() => {
    setSaveError(null);
    setShowRoleErrors(false);
    setEditingUser(null);
    setAccessSummary(null);
    usernameWasEditedRef.current = false;
    usernameSuggestionRequestRef.current += 1;
    usernameSuggestionNameRef.current = "";
    setUsernameWasEdited(false);
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setGeneratedPassword("");
    setPasswordSectionOpen(true);
    form.resetFields();
    form.setFieldsValue({
      status: "enabled",
      whatsapp_phone: "",
      whatsapp_recap_warehouse_ids: [],
      company_ids: selectedCompany ? [Number(selectedCompany)] : [],
      warehouse_ids: [],
    });
    setIsModalVisible(true);
    if (selectedCompany) {
      fetchRoles();
      fetchCompanies();
      fetchWarehouses([selectedCompany]);
    }
  }, [
    form,
    selectedCompany,
    fetchRoles,
    fetchCompanies,
    fetchWarehouses,
  ]);

  const handleEdit = useCallback(async (record) => {
    const editRequestId = ++editRequestRef.current;
    const editCompanyId = selectedCompany;
    setSaveError(null);
    setShowRoleErrors(false);
    usernameWasEditedRef.current = true;
    usernameSuggestionRequestRef.current += 1;
    usernameSuggestionNameRef.current = "";
    setUsernameWasEdited(true);
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setGeneratedPassword("");
    setPasswordSectionOpen(false);
    // Ensure roles and warehouses are fetched *before* setting form values
    // Also check if roles contains SysAdmin if the logged-in user CAN assign it, to ensure data freshness
    const needsFetch =
      selectedCompany &&
      (!roles.length ||
        !companies.length ||
        (hasPermission("Admin.GestionUtilisateurs.assign_sysadmin") &&
          !roles.some((r) => r.name === "SysAdmin" && !r.company_id)));

    if (needsFetch) {
      setModalLoading(true); // Show loading indicator early
      try {
        await Promise.all([fetchRoles(), fetchCompanies()]);
      } catch (fetchError) {
        message.error("Erreur lors du chargement des données nécessaires.");
        setModalLoading(false);
        return; // Stop if essential data failed to load
      }
      // Let the state update and re-render before proceeding further
      // We might need a small delay or useEffect dependency chain in complex cases,
      // but often Promise.all is sufficient if setRoles triggers re-render promptly.
    } else {
    }

    if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
    setEditingUser(record);
    setIsModalVisible(true);
    setModalLoading(true); // Keep modal loading while fetching user details

    try {
      // Fetch specific user details (existing logic)
      const response = await apiClient.get(`/api/users/${record.id}`);
      const userData = response.data;
      if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
      setWhatsappRecapWarehouses(userData.whatsapp_recap_warehouses || []);
      setAccessSummary(
        userData.access_scope === "global"
          ? userData.access_summary || null
          : null,
      );

      // Use helper function to get absolute URL for preview
      const absoluteImageUrl = getImageUrl(userData.profile_image);
      const profileImageFileList = absoluteImageUrl
        ? [
            {
              uid: "-1",
              name: userData.profile_image?.split("/")?.pop() || "image.png",
              status: "done",
              url: absoluteImageUrl, // Use absolute URL
              thumbUrl: absoluteImageUrl, // Use absolute URL for thumbnail
            },
          ]
        : [];

      const assignedWarehouseIds =
        userData.access_scope === "global"
          ? []
          : (userData.assigned_warehouses || []).map((wh) => Number(wh.id));
      const assignedCompanyIds =
        userData.access_scope === "global"
          ? []
          : [
              ...new Set([
                Number(userData.company_id),
                ...(userData.assigned_companies || []).map((company) =>
                  Number(company.id),
                ),
                ...(userData.assigned_warehouses || []).map((warehouse) =>
                  Number(warehouse.company_id),
                ),
              ]),
            ].filter((companyId) => Number.isInteger(companyId) && companyId > 0);

      if (userData.access_scope !== "global") {
        await Promise.all([
          fetchWarehouses(assignedCompanyIds),
          fetchRoles(assignedCompanyIds),
        ]);
      }
      setRoles((previousRoles) => [...new Map([
        ...previousRoles,
        ...(userData.roles || []),
      ].map((role) => [Number(role.id), role])).values()]);

      if (editRequestId !== editRequestRef.current || editCompanyId !== currentCompanyRef.current) return;
      form.resetFields();
      // Set form values AFTER potentially fetching roles
      form.setFieldsValue({
        ...userData,
        civility: userData.civility || undefined,
        whatsapp_phone: userData.whatsapp_phone || "",
        whatsapp_recap_warehouse_ids: userData.whatsapp_recap_warehouse_ids || [],
        company_ids: assignedCompanyIds,
        warehouse_ids: assignedWarehouseIds,
        role_ids:
          userData.role_ids?.map(Number) ||
          (userData.role_id ? [Number(userData.role_id)] : []),
        password: "",
        password_confirmation: "",
        profile_image: profileImageFileList,
      });
    } catch (error) {
      message.error("Impossible de charger les détails de l'utilisateur.");
      setIsModalVisible(false); // Close modal on error
      setEditingUser(null);
      setAccessSummary(null);
    } finally {
      setModalLoading(false); // Stop modal loading indicator
    }
  }, [
    selectedCompany,
    roles,
    companies,
    hasPermission,
    fetchRoles,
    fetchCompanies,
    fetchWarehouses,
    form,
  ]);

  const handleDelete = useCallback(async (id) => {
    try {
      setLoading(true);
      await apiClient.delete(`/api/users/${id}`);
      message.success("Membre du personnel supprimé avec succès !");
      fetchStaff(pagination.current);
    } catch (error) {
      message.error(
        error.response?.data?.error || "Erreur lors de la suppression."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchStaff, pagination.current]);

  const handleCancel = useCallback(() => {
    editRequestRef.current += 1;
    setSaveError(null);
    setShowRoleErrors(false);
    usernameSuggestionRequestRef.current += 1;
    form.setFieldsValue({ password: "", password_confirmation: "" });
    setIsModalVisible(false);
    setEditingUser(null);
    setAccessSummary(null);
    setUsernameSuggestionLoading(false);
    setUsernameSuggestionError("");
    setGeneratedPassword("");
    setPasswordSectionOpen(false);
  }, [form]);

  const handlePasswordSectionChange = useCallback(
    (nextOpen) => {
      if (!editingUser) return;
      setPasswordSectionOpen(nextOpen);
      if (!nextOpen) {
        form.setFieldsValue({ password: "", password_confirmation: "" });
        setGeneratedPassword("");
      }
    },
    [editingUser, form],
  );

  useEffect(() => {
    if (!isModalVisible || selectedRoleIsGlobalSysAdmin || !primaryCompanyId) {
      return;
    }
    const currentCompanyIds = Array.isArray(form.getFieldValue("company_ids"))
      ? form.getFieldValue("company_ids").map(Number)
      : [];
    if (!currentCompanyIds.includes(primaryCompanyId)) {
      form.setFieldValue("company_ids", [
        primaryCompanyId,
        ...currentCompanyIds,
      ]);
    }
  }, [
    form,
    isModalVisible,
    primaryCompanyId,
    selectedRoleIsGlobalSysAdmin,
  ]);

  const handleCompaniesChange = useCallback(
    (values) => {
      const companyIds = [
        ...new Set([
          primaryCompanyId,
          ...(Array.isArray(values) ? values.map(Number) : []),
        ]),
      ].filter((companyId) => Number.isInteger(companyId) && companyId > 0);
      const allowedCompanyIds = new Set(companyIds);
      const warehouseCompanyById = new Map(
        warehouses.map((warehouse) => [
          Number(warehouse.id),
          Number(warehouse.company_id),
        ]),
      );
      const currentWarehouseIds = Array.isArray(
        form.getFieldValue("warehouse_ids"),
      )
        ? form.getFieldValue("warehouse_ids").map(Number)
        : [];

      form.setFieldsValue({
        company_ids: companyIds,
        role_ids: (form.getFieldValue("role_ids") || []).filter((roleId) => {
          const role = roles.find((candidate) => Number(candidate.id) === Number(roleId));
          return role && (!role.company_id || allowedCompanyIds.has(Number(role.company_id)));
        }),
        warehouse_ids: currentWarehouseIds.filter((warehouseId) =>
          allowedCompanyIds.has(warehouseCompanyById.get(warehouseId)),
        ),
      });
    },
    [form, primaryCompanyId, roles, warehouses],
  );

  const handleOk = useCallback(async () => {
    let values;
    setSaveError(null);
    try {
      values = await form.validateFields();
    } catch (error) {
      const firstError = error?.errorFields?.[0];
      const explanation = error?.outOfDate
        ? "Le formulaire a changé pendant la vérification. Vérifiez vos choix puis réessayez."
        : firstError?.errors?.[0] || "Vérifiez les champs obligatoires avant d’enregistrer.";
      setSaveError({ type: "warning", message: explanation });
      setShowRoleErrors(true);
      if (!error?.outOfDate && firstError?.name?.[0] === "role_ids") {
        const missingCompanyId = normalizedSelectedCompanyIds.find((companyId) =>
          !roles.some((role) => Number(role.company_id) === companyId &&
            (form.getFieldValue("role_ids") || []).map(Number).includes(Number(role.id))),
        );
        companyRoleFieldRefs.current.get(missingCompanyId)?.scrollIntoView({
          behavior: "smooth", block: "center",
        });
      } else if (!error?.outOfDate && firstError?.name) {
        form.scrollToField(firstError.name, { behavior: "smooth", block: "center" });
      }
      return;
    }
    try {
      setModalLoading(true);

      const formData = new FormData();
      formData.append("whatsapp_recap_company_id", String(selectedCompany));

      // Populate FormData
      Object.keys(values).forEach((key) => {
        const value = values[key];

        if (key === "profile_image") {
          // Handle file upload
          if (value && value.length > 0 && value[0].originFileObj) {
            formData.append(key, value[0].originFileObj);
          }
          // If no new file, don't append (backend keeps old or null)
        } else if (
          key === "warehouse_ids" ||
          key === "company_ids" ||
          key === "role_ids"
        ) {
          // Les rôles sont toujours transmis. Le périmètre explicite est omis
          // uniquement pour le SysAdmin global.
          if (
            Array.isArray(value) &&
            (key === "role_ids" || !selectedRoleIsGlobalSysAdmin)
          ) {
            value.forEach((id) => formData.append(`${key}[]`, id));
          }
        } else if (key === "whatsapp_recap_warehouse_ids") {
          formData.append(key, JSON.stringify(value || []));
        } else if (key === "civility") {
          formData.append(key, value || "");
        } else if (key === "password") {
          // Only append password if it has a value.
          // For create: validation ensures it has a value if required.
          // For update: if empty, it means don't change password.
          if (value) {
            formData.append(key, value);
          }
        } else if (key === "password_confirmation") {
          // Only append confirmation if the main password field has a value
          if (values["password"] && value) {
            formData.append(key, value);
          }
          // Otherwise skip (no need to confirm an empty or unchanged password)
        } else if (value !== undefined && value !== null) {
          // Append other fields
          formData.append(key, value);
        }
      });

      // Add user_type and company_id
      formData.append("user_type", "staff_members");
      if (primaryCompanyId) {
        formData.append("company_id", primaryCompanyId);
      } else {
        message.error("Aucune entreprise sélectionnée.");
        setModalLoading(false);
        return;
      }

      if (editingUser) {
        // Update existing user (PUT)
        await apiClient.put(`/api/users/${editingUser.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Membre du personnel mis à jour avec succès !");
      } else {
        // Create new user (POST)
        await apiClient.post("/api/users", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("Membre du personnel ajouté avec succès !");
      }

      usernameSuggestionRequestRef.current += 1;
      setIsModalVisible(false);
      invalidateBusinessResources(queryClient, {
        userId: user?.id, companyId: selectedCompany, warehouseId: null, moduleScope: "admin",
      }, "staff-whatsapp-settings");
      setEditingUser(null);
      setAccessSummary(null);
      setUsernameSuggestionLoading(false);
      setUsernameSuggestionError("");
      setGeneratedPassword("");
      setPasswordSectionOpen(false);
      form.setFieldsValue({ password: "", password_confirmation: "" });
      fetchStaff(editingUser ? pagination.current : 1);
    } catch (error) {
      setSaveError({ type: "error", message: getStaffSaveErrorMessage(error) });
    } finally {
      setModalLoading(false);
    }
  }, [
    form,
    editingUser,
    primaryCompanyId,
    fetchStaff,
    pagination.current,
    selectedRoleIsGlobalSysAdmin,
    normalizedSelectedCompanyIds,
    roles,
    queryClient,
    user?.id,
    selectedCompany,
  ]);

  const normFile = useCallback((e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  }, []);

  const showDetailsModal = useCallback(async (record) => {
    setDetailsLoading(true);
    setIsDetailsModalVisible(true);
    if (Number(record.company_id) !== Number(selectedCompany)) {
      setViewingUser(record);
      setDetailsLoading(false);
      return;
    }
    try {
      const response = await apiClient.get(`/api/users/${record.id}`);
      setViewingUser(response.data);
    } catch (error) {
      message.error("Impossible de charger les détails.");
      setIsDetailsModalVisible(false);
    } finally {
      setDetailsLoading(false);
    }
  }, [selectedCompany]);

  const openCompanyRoleEditor = useCallback(async (record) => {
    const companyId = Number(selectedCompany);
    if (!companyId) return;
    const availableRoles = await fetchRoles([companyId]);
    if (!availableRoles.some((role) => Number(role.company_id) === companyId)) return;
    setCompanyRoleIds((record.roles || [])
      .filter((role) => Number(role.company_id) === companyId)
      .map((role) => Number(role.id)));
    setCompanyRoleEditor(record);
  }, [fetchRoles, selectedCompany]);

  const saveCompanyRoles = useCallback(async () => {
    if (!companyRoleEditor || companyRoleIds.length === 0) {
      message.warning("Sélectionnez au moins un rôle pour cette entreprise.");
      return;
    }
    setCompanyRoleSaving(true);
    try {
      await apiClient.put(
        `/api/users/${companyRoleEditor.id}/company-roles/${selectedCompany}`,
        { role_ids: companyRoleIds },
      );
      message.success("Profil de l’entreprise mis à jour.");
      setCompanyRoleEditor(null);
      await fetchStaff(pagination.current);
    } catch (error) {
      message.error(error.response?.data?.message || "Impossible de mettre à jour ce profil.");
    } finally {
      setCompanyRoleSaving(false);
    }
  }, [companyRoleEditor, companyRoleIds, fetchStaff, pagination, selectedCompany]);

  const handleDetailsModalCancel = useCallback(() => {
    setIsDetailsModalVisible(false);
    setViewingUser(null);
  }, []);

  const columns = useMemo(() => [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <Avatar
            src={getImageUrl(record.profile_image)}
            icon={!record.profile_image && <UserOutlined />}
          />
          {text}
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      responsive: ["sm"], // Masqué sur xs
    },
    {
      title: "Établissement Principal",
      dataIndex: "assigned_warehouse_name",
      key: "assigned_warehouse_name",
      responsive: ["sm"], // Masqué sur xs
      render: (name) => name || "N/A",
    },
    {
      title: "Rôles",
      dataIndex: "role_names",
      key: "role_names",
      responsive: ["sm"], // Masqué sur xs
      render: (roleNames, record) => {
        const assignedRoles = Array.isArray(record.roles)
          ? record.roles
          : (Array.isArray(roleNames) ? roleNames : [record.role_name])
              .filter(Boolean)
              .map((name, index) => ({ id: `legacy-${index}`, name }));
        return assignedRoles.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {assignedRoles.map((role) => (
              <Tag key={role.id} color="blue">
                {role.name}
              </Tag>
            ))}
          </Space>
        ) : (
          "N/A"
        );
      },
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "enabled" ? "success" : "error"}>
          {status === "enabled" ? "Activé" : "Désactivé"}
        </Tag>
      ),
      filters: [
        { text: "Activé", value: "enabled" },
        { text: "Désactivé", value: "disabled" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (text, record) => {
        const menu = (
          <Menu>
            {hasPermission(STAFF_PERMISSIONS.VIEW) && (
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => showDetailsModal(record)}
              >
                Voir Détails
              </Menu.Item>
            )}
            {hasPermission(STAFF_PERMISSIONS.ASSIGN_ROLE) &&
              Number(record.id) !== Number(user?.id) &&
              !record.is_superadmin && (
              <Menu.Item
                key="company-profile"
                icon={<SafetyCertificateOutlined />}
                onClick={() => openCompanyRoleEditor(record)}
              >
                Profil de cette entreprise
              </Menu.Item>
            )}
            {hasPermission(STAFF_PERMISSIONS.EDIT) && Number(record.company_id) === Number(selectedCompany) && (
              <Menu.Item
                key="edit"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              >
                Modifier
              </Menu.Item>
            )}
            {hasPermission(STAFF_PERMISSIONS.DELETE) && Number(record.company_id) === Number(selectedCompany) && (
              <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
                <Popconfirm
                  title="Êtes-vous sûr de vouloir supprimer ce membre ?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Oui"
                  cancelText="Non"
                  placement="left"
                >
                  <span style={{ display: "block", width: "100%" }}>
                    Supprimer
                  </span>
                </Popconfirm>
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ], [hasPermission, showDetailsModal, handleEdit, handleDelete, openCompanyRoleEditor, selectedCompany, user?.id]);

  return (
    <div>
      <h2>Les membres du personnel</h2>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {hasPermission(STAFF_PERMISSIONS.CREATE) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              disabled={!selectedCompany}
              className="w-full sm:w-auto"
            >
              Ajouter un nouveau membre du personnel
            </Button>
          )}
          <Button
            icon={<UploadOutlined />}
            disabled
            className="w-full sm:w-auto"
          >
            Importer des membres du personnel
          </Button>
        </div>
        <Input.Search
          placeholder="Rechercher par nom..."
          allowClear
          onSearch={handleSearch}
          onChange={(e) => {
            if (e.target.value === "") {
              handleSearch("");
            }
          }}
          className="w-full sm:w-auto sm:max-w-xs"
        />
      </div>

      <Tabs activeKey={activeTabKey} onChange={handleTabChange}>
        <TabPane tab="Tous" key="all" />
        <TabPane tab="Activés" key="enabled" />
        <TabPane tab="Désactivés" key="disabled" />
      </Tabs>

      <Table
        columns={columns}
        dataSource={staff}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />

      <Modal
        centered
        className="staff-editor-modal"
        title={
          <div className="staff-editor-modal__header">
            <span className="staff-editor-modal__header-icon" aria-hidden="true">
              <UserSwitchOutlined />
            </span>
            <div className="staff-editor-modal__header-copy">
              <div className="staff-editor-modal__heading">
                {editingUser
                  ? "Modifier le membre du personnel"
                  : "Ajouter un membre du personnel"}
              </div>
              <div className="staff-editor-modal__subtitle">
                {editingUser
                  ? "Mettre à jour l’identité, les accès et la sécurité du compte."
                  : "Renseigner l’identité, les accès et la sécurité du nouveau compte."}
              </div>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={
          <>
          {saveError && (
            <Alert
              className="staff-editor-modal__save-error"
              message={saveError.message}
              showIcon
              type={saveError.type}
            />
          )}
          <div className="staff-editor-modal__footer">
            <Button disabled={modalLoading} onClick={handleCancel}>
              Annuler
            </Button>
            <Button
              icon={<SaveOutlined />}
              loading={modalLoading}
              onClick={handleOk}
              type="primary"
            >
              {editingUser
                ? "Enregistrer les modifications"
                : "Créer le membre"}
            </Button>
          </div>
          </>
        }
        width={1040}
      >
        <Spin spinning={modalLoading} tip="Chargement...">
          <Form
            className="staff-editor-modal__form"
            form={form}
            initialValues={{ company_ids: [], warehouse_ids: [] }}
            layout="vertical"
            name="staff_form"
            onValuesChange={handleFormValuesChange}
            scrollToFirstError
          >
            <section
              aria-labelledby="staff-identity-heading"
              className="staff-editor-modal__section"
            >
              <StaffModalSectionHeader
                id="staff-identity-heading"
                icon={<IdcardOutlined />}
                title="Identité et coordonnées"
              />

              <div className="staff-editor-modal__identity-layout">
                <Form.Item
                  className="staff-editor-modal__photo-item"
                  getValueFromEvent={normFile}
                  name="profile_image"
                  valuePropName="fileList"
                >
                  <StaffProfileImageUploader disabled={modalLoading} />
                </Form.Item>

                <div className="staff-editor-modal__identity-fields">
                  <Form.Item label="Civilité" name="civility">
                    <Select
                      allowClear
                      placeholder="Non renseignée"
                      options={[{ value: "mr", label: "M." }, { value: "ms", label: "Mme" }]}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Nom"
                    name="name"
                    rules={[
                      { required: true, message: "Veuillez entrer le nom !" },
                    ]}
                  >
                    <Input autoComplete="name" placeholder="Saisir le nom" />
                  </Form.Item>

                  <Form.Item
                    label="Numéro de téléphone"
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez entrer le numéro de téléphone !",
                      },
                    ]}
                  >
                    <Input
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder="Saisir le numéro de téléphone"
                    />
                  </Form.Item>

                  <Form.Item
                    extra="Facultatif. Choisissez ci-dessous les établissements dont cette personne recevra le bilan."
                    label="Numéro WhatsApp"
                    name="whatsapp_phone"
                    rules={[{
                      validator: (_, value) => isValidOptionalWhatsAppPhone(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(WHATSAPP_PHONE_VALIDATION_MESSAGE)),
                    }]}
                  >
                    <WhatsAppPhoneInput phone={watchedPhone} disabled={modalLoading} />
                  </Form.Item>

                  <Form.Item
                    extra="Bilans de l’entreprise sélectionnée. Laissez vide pour désactiver la réception. Les droits du destinataire sont vérifiés à l’enregistrement."
                    label="Établissements des bilans WhatsApp"
                    name="whatsapp_recap_warehouse_ids"
                  >
                    <WhatsAppRecapSettings
                      active={isModalVisible}
                      actorId={user?.id}
                      recipientId={editingUser?.id}
                      assignedIds={selectedWarehouseIds || []}
                      companyId={selectedCompany}
                      disabled={modalLoading}
                      isGlobal={selectedRoleIsGlobalSysAdmin}
                      savedWarehouses={whatsappRecapWarehouses}
                    />
                  </Form.Item>

                  <Form.Item
                    extra={
                      !editingUser && loggedInUserIsSysAdmin
                        ? usernameSuggestionError ||
                          "Proposé automatiquement en @elsagestion.com à partir du nom. Le champ reste modifiable."
                        : undefined
                    }
                    label="Identifiant de connexion"
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez entrer l’identifiant de connexion !",
                      },
                      {
                        type: "email",
                        message: "Format d’identifiant invalide !",
                      },
                    ]}
                  >
                    <Input
                      autoComplete="email"
                      placeholder="prenom.nom@elsagestion.com"
                      suffix={
                        !editingUser && loggedInUserIsSysAdmin ? (
                          <Button
                            aria-label="Proposer un identifiant disponible"
                            disabled={!String(watchedName || "").trim()}
                            htmlType="button"
                            icon={<ReloadOutlined />}
                            loading={usernameSuggestionLoading}
                            onClick={handleSuggestUsername}
                            size="small"
                            type="text"
                          >
                            Proposer
                          </Button>
                        ) : null
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="Statut"
                    name="status"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez sélectionner le statut !",
                      },
                    ]}
                  >
                    <Select placeholder="Sélectionner le statut">
                      <Option value="enabled">
                        <span className="staff-editor-modal__status-option">
                          <span
                            aria-hidden="true"
                            className="staff-editor-modal__status-dot staff-editor-modal__status-dot--active"
                          />
                          Activé
                        </span>
                      </Option>
                      <Option value="disabled">
                        <span className="staff-editor-modal__status-option">
                          <span
                            aria-hidden="true"
                            className="staff-editor-modal__status-dot"
                          />
                          Désactivé
                        </span>
                      </Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    className="staff-editor-modal__field--wide"
                    label="Adresse"
                    name="address"
                  >
                    <Input.TextArea
                      autoComplete="street-address"
                      placeholder="Saisir l’adresse"
                      rows={3}
                    />
                  </Form.Item>
                </div>
              </div>
            </section>

            <section
              aria-labelledby="staff-access-heading"
              className="staff-editor-modal__section"
            >
              <StaffModalSectionHeader
                id="staff-access-heading"
                icon={<SafetyCertificateOutlined />}
                meta={
                  selectedRoleIsGlobalSysAdmin ? (
                    <Tag color="green">Accès global</Tag>
                  ) : (
                    <span>
                      {formatCountLabel(
                        normalizedSelectedCompanyIds.length,
                        "entreprise",
                        "entreprises",
                      )}{" "}
                      ·{" "}
                      {formatCountLabel(
                        normalizedSelectedWarehouseIds.length,
                        "établissement",
                        "établissements",
                      )}
                    </span>
                  )
                }
                title="Accès et autorisations"
              />

              <div className="staff-editor-modal__access-grid">
                <div className="staff-editor-modal__access-role">
                  <Form.Item
                    hidden
                    name="role_ids"
                    rules={[{
                      validator: (_, roleIds) => {
                        if (selectedRoleIsGlobalSysAdmin) return Promise.resolve();
                        const missing = normalizedSelectedCompanyIds.find((companyId) =>
                          !roles.some((role) =>
                            Number(role.company_id) === companyId &&
                            (roleIds || []).map(Number).includes(Number(role.id)),
                          ),
                        );
                        return missing
                          ? Promise.reject(new Error(`Choisissez au moins un rôle pour ${companyById.get(missing)?.name || `l’entreprise ${missing}`}.`))
                          : Promise.resolve();
                      },
                    }]}
                  >
                    <Select mode="multiple" />
                  </Form.Item>
                  {loggedInUserIsSysAdmin && (
                    <Form.Item label="Accès système">
                      <Select
                        allowClear
                        placeholder="Aucun accès système"
                        value={selectedRoleIsGlobalSysAdmin ? selectedRoleIds[0] : undefined}
                        onChange={(roleId) => form.setFieldValue("role_ids", roleId ? [roleId] : [])}
                      >
                        {roles.filter((role) => role.name === "SysAdmin" && !role.company_id)
                          .map((role) => <Option key={role.id} value={Number(role.id)}>SysAdmin global</Option>)}
                      </Select>
                    </Form.Item>
                  )}
                  {!selectedRoleIsGlobalSysAdmin && normalizedSelectedCompanyIds.map((companyId) => {
                    const companyRoles = roles.filter((role) => Number(role.company_id) === companyId);
                    const currentIds = selectedRoleIds.map(Number).filter((roleId) =>
                      companyRoles.some((role) => Number(role.id) === roleId),
                    );
                    return (
                      <div key={companyId} ref={(element) => {
                        if (element) companyRoleFieldRefs.current.set(companyId, element);
                        else companyRoleFieldRefs.current.delete(companyId);
                      }}>
                      <Form.Item
                        label={`Profil — ${companyById.get(companyId)?.name || `Entreprise ${companyId}`}`}
                        required
                        validateStatus={showRoleErrors && !currentIds.length ? "error" : undefined}
                        help={showRoleErrors && !currentIds.length
                          ? `Choisissez au moins un rôle pour ${companyById.get(companyId)?.name || `l’entreprise ${companyId}`}.`
                          : undefined}
                        extra={unavailableRoleCompanyIds.includes(companyId)
                          ? "Profil consultable ici. Basculez sur cette entreprise pour le modifier."
                          : currentIds.length
                            ? "Les permissions de ces rôles se cumulent dans cette entreprise."
                            : "Choisissez au moins un rôle pour cette entreprise."}
                      >
                        <Select
                          aria-label={`Profil — ${companyById.get(companyId)?.name || `Entreprise ${companyId}`}`}
                          aria-invalid={showRoleErrors && !currentIds.length}
                          mode="multiple"
                          placeholder="Sélectionner un ou plusieurs rôles"
                          value={currentIds}
                          loading={!roles.length}
                          disabled={!companyRoles.length || unavailableRoleCompanyIds.includes(companyId)}
                          onChange={(nextIds) => {
                            const otherIds = selectedRoleIds.map(Number).filter((roleId) =>
                              !companyRoles.some((role) => Number(role.id) === roleId) &&
                              !roles.some((role) => Number(role.id) === roleId && role.name === "SysAdmin" && !role.company_id),
                            );
                            form.setFieldValue("role_ids", [...otherIds, ...nextIds]);
                          }}
                          allowClear
                          maxTagCount="responsive"
                        >
                          {companyRoles.map((role) => <Option key={role.id} value={Number(role.id)}>{role.name}</Option>)}
                        </Select>
                      </Form.Item>
                      </div>
                    );
                  })}
                </div>

                {selectedRoleIsGlobalSysAdmin ? (
                  <Form.Item
                    className="staff-editor-modal__global-access-item"
                    label="Périmètre d’accès"
                  >
                    <div className="staff-editor-modal__global-access">
                      <Space align="start" size={12}>
                        <GlobalOutlined />
                        <div>
                          <Text strong>Accès global</Text>
                          <div>
                            Toutes les entreprises et tous les établissements
                          </div>
                          {accessSummary && (
                            <Text type="secondary">
                              {accessSummary.company_count} entreprise(s) · {" "}
                              {accessSummary.warehouse_count} établissement(s)
                            </Text>
                          )}
                          <Text type="secondary">
                            Les nouveaux établissements seront accessibles
                            automatiquement.
                          </Text>
                        </div>
                      </Space>
                    </div>
                  </Form.Item>
                ) : (
                  <>
                    <Form.Item
                      className="staff-editor-modal__access-companies"
                      extra={
                        <div className="staff-editor-modal__field-support">
                          <span>L’entreprise principale reste obligatoire.</span>
                          {selectedCompanyItems.length > 0 ? (
                            <Popover
                              content={
                                <AccessSelectionPopover
                                  items={selectedCompanyItems}
                                  title="Entreprises accessibles"
                                />
                              }
                              placement="bottomLeft"
                              trigger="click"
                            >
                              <Button
                                aria-label={`Voir les entreprises sélectionnées (${selectedCompanyItems.length})`}
                                icon={<BankOutlined />}
                                size="small"
                                type="link"
                              >
                                {formatViewLabel(
                                  selectedCompanyItems.length,
                                  "l’entreprise",
                                  "entreprises",
                                )}
                              </Button>
                            </Popover>
                          ) : null}
                        </div>
                      }
                      label="Entreprises accessibles"
                      name="company_ids"
                      preserve={false}
                      rules={[
                        {
                          required: true,
                          message: "Sélectionnez au moins l'entreprise principale.",
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Sélectionner les entreprises..."
                        loading={!companies.length && selectedCompany}
                        disabled={!selectedCompany || companies.length === 0}
                        onChange={handleCompaniesChange}
                        showSearch
                        maxTagCount={2}
                        maxTagPlaceholder={(omittedValues) =>
                          `+ ${omittedValues.length} autres`
                        }
                        filterOption={(input, option) =>
                          String(option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        optionFilterProp="children"
                      >
                        {companies.map((company) => {
                          const companyId = Number(company.id);
                          const isPrimary = companyId === primaryCompanyId;
                          const isUsable = ["active", "trial"].includes(
                            String(company.status || "active").toLowerCase(),
                          );
                          const isAlreadySelected =
                            normalizedSelectedCompanyIds.includes(companyId);
                          return (
                            <Option
                              key={company.id}
                              value={companyId}
                              disabled={
                                isPrimary || (!isUsable && !isAlreadySelected)
                              }
                            >
                              {company.name}
                              {isPrimary ? " — principale" : ""}
                              {!isUsable ? " — inactive" : ""}
                            </Option>
                          );
                        })}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      className="staff-editor-modal__access-warehouses"
                      dependencies={["company_ids"]}
                      extra={
                        <div className="staff-editor-modal__field-support">
                          <span>
                            Au moins un établissement doit être sélectionné dans
                            chaque entreprise.
                          </span>
                          {selectedWarehouseItems.length > 0 ? (
                            <Popover
                              content={
                                <AccessSelectionPopover
                                  items={selectedWarehouseItems}
                                  title="Établissements autorisés"
                                />
                              }
                              placement="bottomLeft"
                              trigger="click"
                            >
                              <Button
                                aria-label={`Voir les établissements sélectionnés (${selectedWarehouseItems.length})`}
                                icon={<ShopOutlined />}
                                size="small"
                                type="link"
                              >
                                {formatViewLabel(
                                  selectedWarehouseItems.length,
                                  "l’établissement",
                                  "établissements",
                                )}
                              </Button>
                            </Popover>
                          ) : null}
                        </div>
                      }
                      label="Établissements autorisés"
                      name="warehouse_ids"
                      preserve={false}
                      rules={[
                        {
                          validator: (_, value) => {
                            const selectedWarehouseIds = Array.isArray(value)
                              ? value.map(Number)
                              : [];
                            if (selectedWarehouseIds.length === 0) {
                              return Promise.reject(
                                new Error("Sélectionnez au moins un établissement."),
                              );
                            }
                            const coveredCompanyIds = new Set(
                              warehouses
                                .filter((warehouse) =>
                                  selectedWarehouseIds.includes(Number(warehouse.id)),
                                )
                                .map((warehouse) => Number(warehouse.company_id)),
                            );
                            const missingCompany = normalizedSelectedCompanyIds.find(
                              (companyId) => !coveredCompanyIds.has(companyId),
                            );
                            if (missingCompany) {
                              return Promise.reject(
                                new Error(
                                  `Sélectionnez un établissement pour ${companyById.get(missingCompany)?.name || "chaque entreprise"}.`,
                                ),
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Sélectionner les établissements autorisés..."
                        loading={scopeLoading}
                        disabled={
                          !selectedCompany ||
                          scopeLoading ||
                          warehouses.length === 0
                        }
                        allowClear
                        showSearch
                        maxTagCount={2}
                        maxTagPlaceholder={(omittedValues) =>
                          `+ ${omittedValues.length} autres`
                        }
                        filterOption={(input, option) =>
                          String(option?.children || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        optionFilterProp="children"
                      >
                        {normalizedSelectedCompanyIds.map((companyId) => (
                          <OptGroup
                            key={companyId}
                            label={
                              companyById.get(companyId)?.name ||
                              `Entreprise ID ${companyId}`
                            }
                          >
                            {(warehousesByCompany.get(companyId) || []).map((wh) => (
                              <Option key={wh.id} value={Number(wh.id)}>
                                {wh.name}
                              </Option>
                            ))}
                          </OptGroup>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                )}
              </div>
            </section>

            <section
              aria-labelledby="staff-security-heading"
              className="staff-editor-modal__section staff-editor-modal__section--security"
            >
              <StaffModalSectionHeader
                id="staff-security-heading"
                icon={<LockOutlined />}
                title="Sécurité du compte"
              />

              <div className="staff-editor-modal__security-toggle">
                {editingUser ? (
                  <button
                    aria-expanded={passwordSectionOpen}
                    className="staff-editor-modal__security-copy"
                    onClick={() =>
                      handlePasswordSectionChange(!passwordSectionOpen)
                    }
                    type="button"
                  >
                    <strong>Modifier le mot de passe</strong>
                    <span>
                      Facultatif — laissez désactivé pour conserver le mot de
                      passe actuel.
                    </span>
                  </button>
                ) : (
                  <div className="staff-editor-modal__security-copy">
                    <strong>Définir le mot de passe</strong>
                    <span>
                      Saisissez une valeur sécurisée pour permettre la première
                      connexion.
                    </span>
                  </div>
                )}

                <div className="staff-editor-modal__security-control">
                  {editingUser ? (
                    <>
                      <Switch
                        aria-label="Modifier le mot de passe"
                        checked={passwordSectionOpen}
                        disabled={modalLoading}
                        onChange={handlePasswordSectionChange}
                      />
                      <Button
                        aria-label={
                          passwordSectionOpen
                            ? "Replier les champs de mot de passe"
                            : "Afficher les champs de mot de passe"
                        }
                        disabled={modalLoading}
                        icon={
                          passwordSectionOpen ? (
                            <DownOutlined />
                          ) : (
                            <RightOutlined />
                          )
                        }
                        onClick={() =>
                          handlePasswordSectionChange(!passwordSectionOpen)
                        }
                        type="text"
                      />
                    </>
                  ) : (
                    <Tag color="blue">Requis</Tag>
                  )}
                </div>
              </div>

              {passwordSectionOpen ? (
                <div className="staff-editor-modal__security-fields">
                <Form.Item
                  extra={
                    editingUser
                      ? "Laissez vide pour ne pas changer le mot de passe."
                      : loggedInUserIsSysAdmin
                        ? (
                            <Space direction="vertical" size={4}>
                              <Text type="secondary">
                                Vous pouvez le saisir ou générer une valeur
                                sécurisée de 16 caractères.
                              </Text>
                              <Space size={8} wrap>
                                <Button
                                  htmlType="button"
                                  icon={<KeyOutlined />}
                                  onClick={handleGeneratePassword}
                                  size="small"
                                  type="link"
                                >
                                  Générer un mot de passe
                                </Button>
                                {generatedPassword ? (
                                  <Button
                                    htmlType="button"
                                    icon={<CopyOutlined />}
                                    onClick={handleCopyGeneratedPassword}
                                    size="small"
                                    type="link"
                                  >
                                    Copier
                                  </Button>
                                ) : null}
                              </Space>
                            </Space>
                          )
                        : ""
                  }
                  hasFeedback={Boolean(watchedPassword)}
                  label={editingUser ? "Nouveau mot de passe" : "Mot de passe"}
                  name="password"
                  rules={[
                    {
                      required: !editingUser,
                      message: "Veuillez entrer le mot de passe !",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || value.length >= 6) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "Le mot de passe doit contenir au moins 6 caractères"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder={
                      editingUser
                        ? "Nouveau mot de passe (optionnel)"
                        : "Saisir le mot de passe"
                    }
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
                <Form.Item
                  dependencies={["password"]}
                  hasFeedback={Boolean(watchedPasswordConfirmation)}
                  label={
                    editingUser
                      ? "Confirmer le nouveau mot de passe"
                      : "Confirmer le mot de passe"
                  }
                  name="password_confirmation"
                  rules={[
                    ({ getFieldValue }) => ({
                      required: !!getFieldValue("password"),
                      message: "Veuillez confirmer le mot de passe !",
                    }),
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error(
                            "Les deux mots de passe ne correspondent pas !"
                          )
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder={
                      editingUser
                        ? "Confirmer le nouveau mot de passe"
                        : "Confirmer le mot de passe"
                    }
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                  />
                </Form.Item>
                </div>
              ) : null}
            </section>
          </Form>
        </Spin>
      </Modal>

      <Modal
        title={`Profil dans ${companyById.get(Number(selectedCompany))?.name || "cette entreprise"}`}
        open={Boolean(companyRoleEditor)}
        onCancel={() => setCompanyRoleEditor(null)}
        onOk={saveCompanyRoles}
        okText="Enregistrer le profil"
        okButtonProps={{ disabled: !companyRoleIds.length, loading: companyRoleSaving }}
        destroyOnClose
      >
        <Text>{companyRoleEditor?.name}</Text>
        <Form.Item label="Rôles de cette entreprise" required style={{ marginTop: 16 }}>
          <Select
            mode="multiple"
            value={companyRoleIds}
            onChange={setCompanyRoleIds}
            placeholder="Choisir au moins un rôle"
            allowClear
          >
            {roles.filter((role) => Number(role.company_id) === Number(selectedCompany))
              .map((role) => <Option key={role.id} value={Number(role.id)}>{role.name}</Option>)}
          </Select>
        </Form.Item>
        <Text type="secondary">Les profils des autres entreprises restent inchangés.</Text>
      </Modal>

      <Modal
        title="Détails du membre du personnel"
        visible={isDetailsModalVisible}
        onCancel={handleDetailsModalCancel}
        footer={[
          <Button key="close" onClick={handleDetailsModalCancel}>
            Fermer
          </Button>,
        ]}
        width={isSmallScreen ? "95%" : 600} // Responsive width
      >
        <Spin spinning={detailsLoading} tip="Chargement...">
          {viewingUser ? (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Image">
                <Avatar
                  size={64}
                  src={viewingUser.profile_image}
                  icon={<UserOutlined />}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Nom">
                {viewingUser.name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {viewingUser.email}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {viewingUser.phone || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="WhatsApp">
                {viewingUser.whatsapp_phone || "Non renseigné"}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag
                  color={viewingUser.status === "enabled" ? "success" : "error"}
                >
                  {viewingUser.status === "enabled" ? "Activé" : "Désactivé"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Rôles">
                {viewingUser.roles?.length ? (
                  <Space size={[4, 4]} wrap>
                    {viewingUser.roles.map((role) => (
                      <Tag key={role.id} color="blue">
                        {role.name}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  viewingUser.role_name || "N/A"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Adresse">
                {viewingUser.address || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Périmètre d'accès">
                {viewingUser.access_scope === "global" ||
                viewingUser.is_superadmin === 1 ? (
                  <Space direction="vertical" size={2}>
                    <Tag icon={<GlobalOutlined />} color="success">
                      Accès global
                    </Tag>
                    <Text>Toutes les entreprises et tous les établissements</Text>
                    {viewingUser.access_summary && (
                      <Text type="secondary">
                        {viewingUser.access_summary.company_count} entreprise(s) ·{" "}
                        {viewingUser.access_summary.warehouse_count} établissement(s)
                      </Text>
                    )}
                  </Space>
                ) : viewingUser.assigned_warehouses &&
                  viewingUser.assigned_warehouses.length > 0 ? (
                  <List
                    size="small"
                    dataSource={viewingUser.assigned_warehouses}
                    renderItem={(item) => (
                      <List.Item>
                        {item.name}
                        {item.company_name ? ` — ${item.company_name}` : ""}
                      </List.Item>
                    )}
                  />
                ) : (
                  "Aucun"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Créé le">
                {viewingUser.created_at
                  ? new Date(viewingUser.created_at).toLocaleString()
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Modifié le">
                {viewingUser.updated_at
                  ? new Date(viewingUser.updated_at).toLocaleString()
                  : "N/A"}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <p>Impossible de charger les détails.</p>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default GestionStaff;
